/* eslint-disable react/prop-types */
import React from 'react';
import { Button, HStack, Text } from '@chakra-ui/react';
import { useAppSelector, userHasRequiredPermissions } from 'hooks';
import { useGetCurrentCampaignOwnersList } from 'queries/campaigns/campaigns.fetchOwners';
import { Form } from 'components/Form';
import { updateOwner, selectCurrentCampaign } from 'store/campaigns';
import { OWNER_UPDATE_SCHEMA } from './Campaign.schema';
import { ControlledSelect } from 'components/ControlledFormFields';
import Card from 'layouts/Card';
import { isUserBuyer } from 'store/user/user.selectors';
import { CAN_EDIT_ASSIGNEE } from 'consts/permissions';
import { useTranslation } from 'react-i18next';

const CardWrapper = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  return (
    <Card size='md' variant='unstyled'>
      <HStack justifyContent='space-between'>
        {/* <Text fontSize='xs' fontWeight='medium'>
          {t('campaign.owner', 'Opiekun kampanii')}
        </Text> */}
        {children}
      </HStack>
    </Card>
  );
};

interface CardInfoProps {
  name: string;
  canModify?: boolean;
  onEdit?: () => void;
}

const CardInfo = ({ name, canModify, onEdit }: CardInfoProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <HStack justifyContent='space-between'>
      <Text fontWeight='normal'>{name}</Text>
      {canModify && onEdit && (
        <Button size='sm' onClick={onEdit}>
          {t('change', 'Zmień')}
        </Button>
      )}
    </HStack>
  );
};

const ViewMode = (): JSX.Element => {
  const { t } = useTranslation();
  const campaign = useAppSelector(selectCurrentCampaign);

  return (
    <CardWrapper>
      <CardInfo
        name={campaign?.ownerStr || t('campaign.noOwner', 'Brak opiekuna')}
      />
    </CardWrapper>
  );
};

const EditMode = ({ campaignId }: { campaignId: number }): JSX.Element => {
  const { t } = useTranslation();
  const campaign = useAppSelector(selectCurrentCampaign);
  const [isEditing, setIsEditing] = React.useState(false);

  const {
    data: users,
    isLoading: isUsersLoading,
  } = useGetCurrentCampaignOwnersList(campaignId);

  const filteredOptions = React.useMemo(() => {
    if (!users || isUsersLoading) return [];
    const filtered = users.data.map((user: any) => ({
      id: user.id,
      name: user.lastName + ' ' + user.firstName,
    }));
    if (filtered.length === 0) return [];
    const [firstItem, ...rest] = filtered;
    const sorted = rest.sort((a: { name: string }, b: { name: string }) =>
      a.name.localeCompare(b.name, 'pl-PL'),
    );
    return [firstItem, ...sorted];
  }, [users, isUsersLoading]);

  if (isEditing) {
    return (
      <CardWrapper>
        <Form
          dispatchMethod={updateOwner}
          schema={OWNER_UPDATE_SCHEMA}
          defaultValues={{ owner: campaign?.owner, campaignId }}
          onSubmit={() => setIsEditing(false)}
          onSubmitSuccess={() => setIsEditing(false)}
        >
          <HStack align='stretch' spacing={3} width='100%'>
            <ControlledSelect name='owner' options={filteredOptions} />
            <HStack justifyContent='flex-end'>
              <Button size='sm' onClick={() => setIsEditing(false)}>
                {t('cancel', 'Anuluj')}
              </Button>
              <Button size='sm' type='submit' variant='primary'>
                {t('confirm', 'Potwierdź')}
              </Button>
            </HStack>
          </HStack>
        </Form>
      </CardWrapper>
    );
  }

  return (
    <CardWrapper>
      <CardInfo
        name={campaign?.ownerStr || t('campaign.noOwner', 'Brak opiekuna')}
        onEdit={() => setIsEditing(true)}
        canModify={true}
      />
    </CardWrapper>
  );
};

const CampaignOwnerEdit = ({
  campaignId,
}: {
  campaignId: number;
}): JSX.Element => {
  const canEdit: boolean = userHasRequiredPermissions([CAN_EDIT_ASSIGNEE]);
  const isBuyer: boolean = useAppSelector(isUserBuyer);

  if (canEdit && isBuyer) {
    return <EditMode campaignId={campaignId} />;
  }

  if (isBuyer) {
    return <ViewMode />;
  }

  return <></>;
};
CampaignOwnerEdit.displayName = 'CampaignOwnerEdit';
export { CampaignOwnerEdit };
